import {Component, computed, inject, signal} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, concatMap, of, throwError} from 'rxjs';
import {FormMode} from '../../../shared/types/common';
import {DrawerFormBase} from '../drawer-form-base';
import {ManagementStore} from '../management.store';
import {ManagementService} from '../management.service';
import {AuthorizationService} from '../../../services/authorization.service';
import {Permissions} from '../../../shared/permissions';
import {CustomValidators} from '../../../shared/validators';
import {objectArrayToSelectOptions} from '../../../utils/mappings';
import {Account, AccountPayload, AccountUsersResponse, BrandSummary, SubscriptionStatus} from '../types';
import {subscriptionLabel} from '../subscription.utils';
import {ConfirmTypeToDeleteModalComponent} from '../../../shared/components/confirm-type-to-delete-modal/confirm-type-to-delete-modal.component';
import {TitledInputComponent} from '../../../shared/components/titled-input/titled-input.component';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {SelectMenuComponent, SelectMode} from '../../../shared/components/select-menu/select-menu.component';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';

/** Marks a trial-end save failure that happened after the account fields were already persisted. */
class SubscriptionStepError {
  constructor(public readonly cause: unknown) {}
}

@Component({
  selector: 'app-account-drawer',
  imports: [ReactiveFormsModule, CommonModule, TitledInputComponent, ButtonComponent, SelectMenuComponent, LoaderComponent],
  templateUrl: './account-drawer.component.html',
  styleUrl: './account-drawer.component.scss',
})
export class AccountDrawerComponent extends DrawerFormBase<Account> {
  public form: FormGroup<{
    name: FormControl<string | null>;
    key: FormControl<string | null>;
    brands: FormControl<string[] | null>;
    trialEnd: FormControl<string | null>;
  }>;
  public readonly configs = {
    [FormMode.ADD]: {title: 'Add account', submitText: 'Add', submit: () => this.addAccount()},
    [FormMode.EDIT]: {title: 'Edit account', submitText: 'Update', submit: () => this.editAccount()},
  };

  public accountUsers?: string[];
  public canDelete = false;
  // Server enforces write:account on both account edits and the subscription endpoint — hide/disable
  // the subscription controls for users who would only get a 403 (same precedent as canDelete).
  public canEditSubscription = false;
  protected readonly SelectMode = SelectMode;

  // --- Subscription section (edit mode). The trial-end date is a regular form field — the
  // drawer's single footer Update button persists it together with the other fields (no second
  // save button). Convert-to-Active is the only in-section action; it updates the drawer in
  // place, and the store's replaceRow keeps the table row in sync either way.
  public readonly subscriptionSaving = signal(false);
  public readonly subscriptionStatus = signal<SubscriptionStatus | undefined>(undefined);
  public readonly minTrialEndDate = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  /** The persisted trial end (yyyy-MM-dd) — submit only calls the subscription endpoint when the field differs from this. */
  private savedTrialEndDate: string | null = null;

  public get subscriptionChipLabel(): string {
    return subscriptionLabel(this.subscriptionStatus());
  }

  public get subscriptionChipClass(): string {
    return (this.subscriptionStatus() ?? 'NONE').toLowerCase();
  }

  public get canConvert(): boolean {
    const status = this.subscriptionStatus();
    return !!status && status !== 'ACTIVE';
  }

  public get isBusy(): boolean {
    return this.saving() || this.subscriptionSaving();
  }

  private get trialEndChanged(): boolean {
    const value = this.trialEnd.value;
    return !!value && value !== this.savedTrialEndDate;
  }

  public convertToActive(): void {
    if (!this.entity || this.isBusy) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmTypeToDeleteModalComponent, {
      data: {
        header: 'Convert account to Active',
        description: `This marks ${this.entity.name} as a paid (Active) account. Members currently on Trial or Expired become paid users and lose the trial limitations. Type the account name to confirm.`,
        confirmLabel: 'Convert account',
        expectedValue: this.entity.name,
      },
      panelClass: 'management-modal',
      autoFocus: false,
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.subscriptionSaving.set(true);
      this.store.updateAccountSubscription(this.entity!.accountKey, {action: 'convert'})
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: result => {
            this.applySubscriptionResult(result);
            this.subscriptionSaving.set(false);
            this.utilService.openSnackBar(`Account "${this.entity!.name}" converted to Active`, {position: 'center', variant: 'success'});
          },
          error: error => {
            this.subscriptionSaving.set(false);
            this.onError(error);
          },
        });
    });
  }

  private applySubscriptionResult(result: Account): void {
    this.subscriptionStatus.set(result.subscriptionStatus);
    this.savedTrialEndDate = result.trialEndsAt ? result.trialEndsAt.slice(0, 10) : null;
    this.trialEnd.setValue(this.savedTrialEndDate, {emitEvent: false});
    this.trialEnd.markAsPristine();
  }

  // Options derived from the store's cached brand summaries — reactive, so they fill in as soon as
  // the (lazily fetched, then cached) list resolves.
  protected readonly brandOptions = computed(() => objectArrayToSelectOptions(this.store.brandSummaries(), 'brandKey', 'name'));

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(ManagementStore);
  private readonly api = inject(ManagementService);
  private readonly authorizationService = inject(AuthorizationService);

  constructor() {
    super();
    this.canDelete = this.authorizationService.hasPermissions([Permissions.DELETE_ACCOUNT]);
    this.canEditSubscription = this.authorizationService.hasPermissions([Permissions.WRITE_ACCOUNT]);
    this.store.ensureBrandSummaries();

    this.form = this.fb.group({
      key: new FormControl({value: '', disabled: !!this.entity}, [CustomValidators.required('Account key'), CustomValidators.brandKey('Account key')]),
      name: new FormControl('', [CustomValidators.required('Account name')]),
      brands: new FormControl<string[]>([]),
      // A NEW date must be in the future (mirrors the server's validation); an unchanged date stays
      // valid even when past (lets an EXPIRED account keep showing its old end date untouched); and
      // clearing a saved date is blocked — the server has no "remove trial end" semantics, so an
      // explicit error beats silently reverting the field after save.
      trialEnd: new FormControl<string | null>(null, [control => {
        if (!control.value) {
          return this.savedTrialEndDate ? {trialEndCleared: true} : null;
        }
        return control.value === this.savedTrialEndDate || control.value >= this.minTrialEndDate ? null : {pastTrialEnd: true};
      }]),
    });

    if (this.entity) {
      this.currentMode = FormMode.EDIT;
      this.api.getAccountUsers(this.entity.accountKey)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: users => (this.accountUsers = this.userResponseToStringArray(users)),
          error: () => (this.accountUsers = []),
        });
      this.form.patchValue({
        ...this.entity,
        key: this.entity.accountKey,
        brands: ((this.entity.brands ?? []) as unknown as BrandSummary[]).map(brand => brand.brandKey),
      });
      this.subscriptionStatus.set(this.entity.subscriptionStatus);
      this.savedTrialEndDate = this.entity.trialEndsAt ? this.entity.trialEndsAt.slice(0, 10) : null;
      this.trialEnd.setValue(this.savedTrialEndDate, {emitEvent: false});
      if (!this.canEditSubscription) {
        this.trialEnd.disable({emitEvent: false});
      }
    }
    this.trackGeneratedKey(this.name, this.key);
  }

  get key(): FormControl {
    return this.form.get('key') as FormControl;
  }

  get name(): FormControl {
    return this.form.get('name') as FormControl;
  }

  get brands(): FormControl<string[]> {
    return this.form.get('brands') as FormControl<string[]>;
  }

  get trialEnd(): FormControl<string | null> {
    return this.form.get('trialEnd') as FormControl<string | null>;
  }

  submit(): void {
    if (this.saving() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.configs[this.currentMode].submit();
  }

  private addAccount(): void {
    this.store.addAccount(this.getAccountPayload())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => this.onSuccess(`Account "${result.name}" added successfully`),
        error: error => this.onError(error),
      });
  }

  private editAccount(): void {
    // The trial-end date rides the same Update as every other field: persist the account first,
    // then (only when the date actually changed) the subscription endpoint, so one click = one
    // consistent result and the last response wins in the store's row replacement.
    const trialEndToSave = this.trialEndChanged ? this.trialEnd.value : null;
    this.store.editAccount(this.getAccountPayload())
      .pipe(
        concatMap(result => (trialEndToSave
          ? this.store.updateAccountSubscription(this.entity!.accountKey, {trialEndsAt: trialEndToSave}).pipe(
              // The account fields are already persisted (and the table row replaced) by the time
              // this step can fail — flag it so it isn't reported as a full save failure.
              catchError(error => throwError(() => new SubscriptionStepError(error)))
            )
          : of(result))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          this.applySubscriptionResult(result);
          this.onSuccess(`Account "${result.name}" updated successfully`);
        },
        error: error => {
          if (error instanceof SubscriptionStepError) {
            this.saving.set(false);
            this.utilService.openSnackBar(
              `Account "${this.entity!.name}" was updated, but saving the trial end date failed. Please try the date change again.`,
              {variant: 'error', position: 'center'},
            );
            return;
          }
          this.onError(error);
        },
      });
  }

  deleteAccount(): void {
    if (!this.entity) {
      return;
    }
    // Only empty accounts can be deleted. The row already carries its users (loaded with elaborate),
    // so gate synchronously; fall back to the fetched users count. Surface the reason only on an
    // actual delete attempt, not as a standing warning while editing.
    if ((this.entity.userIds?.length ?? this.accountUsers?.length ?? 0) > 0) {
      this.utilService.openSnackBar('Remove all users before this account can be deleted.', {variant: 'error', position: 'center'});
      return;
    }
    this.confirmAndDelete(
      {
        header: 'Delete account',
        description: `This permanently deletes the account ${this.entity.name} (${this.entity.accountKey}) and its data. This action is irreversible.`,
        confirmLabel: 'Delete account',
        expectedValue: this.entity.name,
      },
      () => this.store.deleteAccount(this.entity!.accountKey),
      `Account "${this.entity.name}" deleted successfully`,
    );
  }

  private userResponseToStringArray(users: AccountUsersResponse): string[] {
    return users.accountUsers.map(user => `${user.firstName} ${user.lastName} (${user.email})`);
  }

  private getAccountPayload(): AccountPayload {
    const form = this.form.getRawValue();
    return {accountKey: form.key, name: form.name, brandKeys: form.brands} as AccountPayload;
  }
}
